<template>
  <div class="debt-installment-warranty">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo" />
    <b-row>
      <b-col md="12" lg="12" sm="12">
        <KTCodePreview v-bind:title="' '">
          <template v-slot:toolbar>
            <div class="row">
              <div>
                <b-dropdown size="sm" id="dropdown-warranty-1" right>
                  <template slot="button-content">
                    <i style="font-size: 1rem" class="fas fa-cog"></i>
                    <span class="font-weight-bolder">Thao tác</span>
                  </template>
                  <b-dropdown-item @click="showExcelModal">
                    <span>
                      <i style="font-size: 1rem" class="far fa-file-excel"></i>
                      &nbsp; Xuất Excel
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </template>
          <template v-slot:preview>
            <DebtInstallmentWarrantyFilter
              v-model="filters"
              :installment-options="availableInstallments"
              :store-options="availableStores"
              :is-loading="onLoadingList"
              @apply-filters="onApplyFilters"
            />
            <DebtInstallmentWarrantyTable
              :data="mainList.dataset"
              :is-loading="onLoadingList"
              :totals="calculatedTotals"
              @cash-payment="onCashPayment"
            />
            <DebtInstallmentWarrantyPagination
              :total="mainList.total"
              :total-pages="mainList.pages"
              :current-page="apiParams.page"
              :page-size="apiParams.pageSize"
              :filter-params="getFilterParams()"
              @page-change="onPageChange"
            />
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
    <DebtExcel :param="apiParams" mode="warranty-items" />
  </div>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { getAddDate, getSubtractDate } from '@/utils/date';
import { getToastInstance } from '@/utils/toastHelper';
import KTCodePreview from '@/view/content/CodePreview.vue';
import Loader from '@/view/content/Loader.vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';

import DebtInstallmentWarrantyFilter from './components/DebtInstallmentWarrantyFilter.vue';
import DebtInstallmentWarrantyPagination from './components/DebtInstallmentWarrantyPagination.vue';
import DebtInstallmentWarrantyTable from './components/DebtInstallmentWarrantyTable.vue';
import DebtExcel from '@/view/pages/debt/shared/components/DebtExcel.vue';
import DebtInstallmentWarrantyService from './services/DebtInstallmentWarrantyService.js';

const MIN_RANGE_DATE = getSubtractDate(7);
const MAX_RANGE_DATE = getAddDate();

export default {
  name: 'DebtInstallmentWarrantyList',
  components: {
    KTCodePreview,
    Loader,
    DebtInstallmentWarrantyFilter,
    DebtInstallmentWarrantyTable,
    DebtInstallmentWarrantyPagination,
    DebtExcel,
  },
  data() {
    return {
      title: 'Công nợ trả góp bảo hành theo ngày',
      mainList: { dataset: [], total: 0, pages: 1 },
      filters: {
        fromDate: MIN_RANGE_DATE,
        toDate: MAX_RANGE_DATE,
        storeId: null,
        contactId: null,
        contractCode: null,
        docId: null,
        customerMobile: null,
        isFullPaid: null,
      },
      apiParams: {
        page: 1,
        pageSize: 10,
        fromDate: MIN_RANGE_DATE,
        toDate: MAX_RANGE_DATE,
        storeId: null,
        contactId: null,
        contractCode: null,
        docId: null,
        customerMobile: null,
        isFullPaid: null,
      },
      onLoadingList: false,
      availableInstallments: [],
      availableStores: [],
    };
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    calculatedTotals() {
      return DebtInstallmentWarrantyService.calculateSummaryTotals(this.mainList.dataset);
    },
  },
  async created() {
    try {
      this.loadBreadcrumb();
      this.getFiltersFromUrl();
      await this.loadInitialData();
      await this.fetchMainData();
    } catch (error) {
      console.error('Error in component creation:', error);
      this.makeToastFailure('Có lỗi xảy ra khi tải dữ liệu');
    }
  },
  methods: {
    ...getToastInstance(),
    async loadInitialData() {
      try {
        const { installments, stores } = await DebtInstallmentWarrantyService.loadInitialData();
        this.availableInstallments = installments;
        this.availableStores = stores;
        if (stores && stores.length === 2) {
          const actualStore = stores.find((s) => s.id !== null);
          if (actualStore) {
            this.filters.storeId = actualStore.id;
            this.apiParams.storeId = actualStore.id;
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        this.makeToastFailure('Không thể tải dữ liệu khởi tạo');
      }
    },
    async fetchMainData() {
      try {
        this.onLoadingList = true;
        const params = DebtInstallmentWarrantyService.buildApiParameters(this.filters, this.$route);
        this.apiParams = { ...this.apiParams, ...params };
        const response = await DebtInstallmentWarrantyService.getInstallmentWarrantyTransactions(this.apiParams);
        this.mainList = response;
      } catch (error) {
        console.error('Error fetching main data:', error);
        const message = error.response?.data?.message || 'Lỗi khi tải dữ liệu';
        this.makeToastFailure(message);
      } finally {
        this.onLoadingList = false;
      }
    },
    async onApplyFilters(newFilters) {
      try {
        const validation = DebtInstallmentWarrantyService.validateFilters(newFilters);
        if (!validation.isValid) {
          validation.errors.forEach((err) => this.makeToastFailure(err));
          return;
        }
        this.filters = { ...newFilters };
        this.apiParams.page = 1;
        this.updateUrl();
        await this.fetchMainData();
      } catch (error) {
        console.error('Error applying filters:', error);
        this.makeToastFailure('Có lỗi xảy ra khi áp dụng bộ lọc');
      }
    },
    async onPageChange(page) {
      try {
        this.apiParams.page = page;
        await this.fetchMainData();
      } catch (error) {
        this.makeToastFailure('Có lỗi xảy ra khi chuyển trang');
      }
    },
    onCashPayment({ warranty, item }) {
      const note = DebtInstallmentWarrantyService.generatePaymentNote(warranty, item);
      this.$router.push({
        name: 'transaction',
        params: {
          mainModelProp: {
            contactType: '3',
            reconciliationDocType: '8',
            reconciliationDocId: String(warranty.id),
            type: '1',
            contactId: item.paymentRefId,
            contactName: item.paymentName,
            amount: item.paymentAmount,
            note,
          },
          mode: 'cash',
          action: 'add',
          id: 'new',
        },
      });
    },
    showExcelModal() {
      this.$bvModal.show('modal-debt-excel');
    },
    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kế toán', route: 'accountant' },
        { title: 'Công nợ' },
        { title: this.title },
      ]);
    },
    getFiltersFromUrl() {
      const query = this.$route.query || {};
      if (Object.keys(query).length > 0) {
        this.filters = {
          ...this.filters,
          storeId: query.storeId || null,
          fromDate: query.fromDate || MIN_RANGE_DATE,
          toDate: query.toDate || MAX_RANGE_DATE,
          contactId: query.contactId || null,
          contractCode: query.contractCode || null,
          docId: query.docId || null,
          customerMobile: query.customerMobile || null,
          isFullPaid: query.isFullPaid || null,
        };
        this.apiParams.page = parseInt(query.page, 10) || 1;
      }
    },
    updateUrl() {
      this.$router.push({
        name: 'list-debt-installment-warranty',
        query: _.omitBy(this.getFilterParams(), _.isNil),
      });
    },
    getFilterParams() {
      return {
        storeId: this.filters.storeId,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        contactId: this.filters.contactId,
        contractCode: this.filters.contractCode,
        docId: this.filters.docId,
        customerMobile: this.filters.customerMobile,
        isFullPaid: this.filters.isFullPaid,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-warranty .icon:hover {
  background-color: #90c6fc;
}
</style>
